<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useQuery } from '@vue/apollo-composable'
import {
  EbomPartAllocationSuggestionDecision,
  EbomPartSnoozeStatus,
  EbomPartTreeEbomPartsQuery,
  EbomPartTreeEbomPartsQueryVariables,
} from '@/generated/graphql'
import gql from 'graphql-tag'
import { computed, nextTick, ref, shallowRef, watch, type VNodeRef } from 'vue'
import { VVirtualScroll } from 'vuetify/components'
import EbomPartSnoozeFilters from '@/components/ebom/EbomPartSnoozeFilters.vue'
import ArticleQuantity from '@/components/article/ArticleQuantity.vue'
import useTree, { TreeItemNode } from '@/composables/useTree'
import { sortTree } from '@/app'
import { openTreeRows, type TreeRow } from '@/utils/treeRows'

const props = defineProps<{
  ebomId: string
}>()
const selectedEbomPartId = defineModel<string | undefined>()

const { t } = useI18n()
const { treeRootNodes } = useTree()

export type EbomPartTreeEbomPart = EbomPartTreeEbomPartsQuery['article']['ebom']['ebomParts'][0] & {
  netAllocationMilliQuantity: number
  undecidedSuggestionsCount: number
}
const fetchQuery = useQuery<EbomPartTreeEbomPartsQuery, EbomPartTreeEbomPartsQueryVariables>(
  gql`
    query EbomPartTreeEbomParts($id: ID!) {
      article {
        ebom(id: $id) {
          ebomParts {
            id
            parentId
            articleNumber
            name
            revision
            milliQuantity
            unit
            snoozeStatus
            position
            article {
              id
            }
            articlePath {
              articleNumbers
            }
            stepAllocations {
              milliQuantity
            }
            stepAllocationSuggestions {
              decision
            }
          }
        }
      }
    }
  `,
  () => ({
    id: props.ebomId,
  }),
)

type EbomPartTreeNode = TreeItemNode<EbomPartTreeEbomPart> & {
  children: EbomPartTreeNode[]
  hasPossibleChildren: boolean
  hasAllocationAbberation: boolean
  hasOpenSuggestions: boolean
  hasSnoozeStatuses: EbomPartSnoozeStatus[]
}

const nodeProcessor = (node: TreeItemNode<EbomPartTreeEbomPart>): EbomPartTreeNode => {
  const processedChildren = node.children.map(nodeProcessor)

  const sortedChildren = sortTree(
    processedChildren,
    (n) => n.children.length > 0,
    (n) => n.item.position || n.item.articleNumber,
  )

  const hasAllocationAbberation =
    node.item.netAllocationMilliQuantity != 0 ||
    sortedChildren.some((c) => c.hasAllocationAbberation)
  const hasOpenSuggestions =
    node.item.stepAllocationSuggestions.filter(
      (s) => s.decision == EbomPartAllocationSuggestionDecision.Undecided,
    ).length > 0 || sortedChildren.some((c) => c.hasOpenSuggestions)
  const hasSnoozeStatuses = sortedChildren.reduce(
    (acc, c) => {
      if (!acc.includes(c.item.snoozeStatus)) {
        acc.push(c.item.snoozeStatus)
      }
      return acc
    },
    [node.item.snoozeStatus] as EbomPartSnoozeStatus[],
  )
  const hasPossibleChildren = sortedChildren.length > 0

  return {
    ...node,
    hasPossibleChildren,
    children: sortedChildren,
    hasAllocationAbberation,
    hasOpenSuggestions,
    hasSnoozeStatuses,
  }
}
const ebomParts = computed(() => fetchQuery.result.value?.article.ebom.ebomParts || [])
const rootNodes = computed(() => {
  const raw = ebomParts.value
  const processed = raw.map((p) => {
    const netAllocationMilliQuantity = p.article
      ? p.stepAllocations.reduce((agg, c) => agg + c.milliQuantity, 0) - p.milliQuantity
      : 0
    const undecidedSuggestionsCount = p.stepAllocationSuggestions.filter(
      (s) => s.decision == EbomPartAllocationSuggestionDecision.Undecided,
    ).length
    return {
      ...p,
      netAllocationMilliQuantity,
      undecidedSuggestionsCount,
    }
  })
  const treeNodes = treeRootNodes(processed)

  const sortedRootNodes = sortTree(
    treeNodes,
    (n) => n.children.length > 0,
    (n) => n.item.position || n.item.articleNumber,
  )

  return sortedRootNodes.map(nodeProcessor)
})

const showOnlySnoozedStatuses = ref<EbomPartSnoozeStatus[]>([EbomPartSnoozeStatus.None])
const showOnlyOpenSuggestions = ref(false)
const showOnlyAllocationDisparity = ref(false)
const filterProcessor = (nodes: EbomPartTreeNode[]): EbomPartTreeNode[] =>
  nodes
    .filter((n) => {
      if (!n.hasSnoozeStatuses.some((s) => showOnlySnoozedStatuses.value.includes(s))) {
        return false
      }
      if (showOnlyOpenSuggestions.value && !n.hasOpenSuggestions) {
        return false
      }
      if (showOnlyAllocationDisparity.value && !n.hasAllocationAbberation) {
        return false
      }

      return true
    })
    .map((n) => ({
      ...n,
      children: filterProcessor(n.children),
    }))
const filteredNodes = computed(() => filterProcessor(rootNodes.value))

type EbomPartTreeRow = TreeRow<EbomPartTreeNode>

const openedIds = shallowRef(new Set<string>())
const rows = computed(() =>
  openTreeRows(filteredNodes.value, (n) => openedIds.value.has(n.item.id)),
)
const toggle = (node: EbomPartTreeNode) => {
  if (!node.hasPossibleChildren) return
  const ids = new Set(openedIds.value)
  if (!ids.delete(node.item.id)) ids.add(node.item.id)
  openedIds.value = ids
}

const virtualScroll = ref<VVirtualScroll>()
let activatedInTree = false
const activate = (node: EbomPartTreeNode) => {
  activatedInTree = true
  selectedEbomPartId.value = selectedEbomPartId.value == node.item.id ? undefined : node.item.id
}
const parentIdById = computed(() => new Map(ebomParts.value.map((p) => [p.id, p.parentId])))
// A part selected from elsewhere (e.g. a link from a step) gets its assemblies opened and is
// scrolled into view.
watch(
  [selectedEbomPartId, () => parentIdById.value.size > 0],
  ([id]) => {
    const fromTree = activatedInTree
    activatedInTree = false
    if (!id) return
    const ids = new Set(openedIds.value)
    for (let p = parentIdById.value.get(id); p; p = parentIdById.value.get(p)) ids.add(p)
    openedIds.value = ids
    if (fromTree) return
    // The virtual list only grows to its full height a frame after the rows changed.
    nextTick(() =>
      requestAnimationFrame(() => {
        const index = rows.value.findIndex((r) => r.node.item.id == id)
        if (index >= 0) virtualScroll.value?.scrollToIndex(index)
      }),
    )
  },
  { immediate: true },
)
</script>

<template>
  <v-row>
    <v-col cols="6">
      <h2>
        {{ t('component.ebomPartTree.title') }}
        <v-progress-circular v-if="fetchQuery.loading.value" indeterminate size="20" />
      </h2>
    </v-col>
    <v-col class="mb-3 text-right">
      <ebom-part-snooze-filters v-model="showOnlySnoozedStatuses">
        <template #activator="{ props: activatorProps }">
          <v-btn
            density="compact"
            icon="alarm_off"
            :title="t('component.ebomPartTree.filterSnoozedHint')"
            :variant="showOnlySnoozedStatuses.length == 0 ? 'flat' : 'outlined'"
            v-bind="activatorProps"
          />
        </template>
      </ebom-part-snooze-filters>
      <v-btn
        density="compact"
        class="ml-1"
        :variant="showOnlyOpenSuggestions ? 'outlined' : 'plain'"
        icon="settings_suggest"
        :title="t('component.ebomPartTree.filterOpenSuggestionsHint')"
        @click="showOnlyOpenSuggestions = !showOnlyOpenSuggestions"
      />
      <v-btn
        density="compact"
        :variant="showOnlyAllocationDisparity ? 'outlined' : 'plain'"
        class="ml-1"
        icon="difference"
        :title="t('component.ebomPartTree.filterAllocationDisparityHint')"
        @click="showOnlyAllocationDisparity = !showOnlyAllocationDisparity"
      />
    </v-col>
  </v-row>
  <div style="user-select: none">
    <v-virtual-scroll
      ref="virtualScroll"
      :items="rows"
      :item-key="(row) => row.node.item.id"
      :item-height="48"
      :renderless="true"
    >
      <template #default="{ item: row, itemRef }: { item: EbomPartTreeRow; itemRef?: VNodeRef }">
        <v-list-item
          :ref="itemRef"
          density="compact"
          :active="row.node.item.id == selectedEbomPartId"
          :style="{ paddingInlineStart: `${row.depth * 20 + 4}px` }"
          @click="activate(row.node)"
          @dblclick="toggle(row.node)"
        >
          <template #prepend>
            <v-btn
              v-if="row.node.hasPossibleChildren"
              :icon="openedIds.has(row.node.item.id) ? 'expand_more' : 'chevron_right'"
              variant="text"
              density="compact"
              size="small"
              @click.stop="toggle(row.node)"
              @dblclick.stop
            />
            <span v-else class="toggle-spacer" />
            <v-icon
              density="compact"
              class="mr-2"
              :icon="
                row.node.hasPossibleChildren
                  ? openedIds.has(row.node.item.id)
                    ? 'folder_open'
                    : 'folder'
                  : 'settings'
              "
              :color="
                row.node.children.length && row.node.hasAllocationAbberation ? 'error' : undefined
              "
            />
          </template>
          <div class="title" :title="row.node.item.name">
            {{ row.node.item.articleNumber }}/{{ row.node.item.revision
            }}<span v-if="row.node.item.position"> - {{ row.node.item.position }}</span>
          </div>
          <div class="subtitle" :title="row.node.item.name">
            {{ row.node.item.name }}
          </div>
          <template #append>
            <v-chip
              v-if="row.node.item.undecidedSuggestionsCount > 0"
              density="compact"
              class="count"
            >
              {{ row.node.item.undecidedSuggestionsCount }}
            </v-chip>
            <v-chip
              v-if="!!row.node.item.article"
              :color="
                row.node.item.netAllocationMilliQuantity == 0
                  ? 'success'
                  : row.node.item.snoozeStatus == EbomPartSnoozeStatus.None
                    ? 'error'
                    : 'warning'
              "
              density="compact"
              class="count"
            >
              <v-icon v-if="row.node.item.snoozeStatus != EbomPartSnoozeStatus.None">
                alarm_off
              </v-icon>
              <article-quantity
                :milli-quantity="row.node.item.netAllocationMilliQuantity"
                :unit="row.node.item.unit"
                unit-abbreviated
              />
            </v-chip>
          </template>
        </v-list-item>
      </template>
    </v-virtual-scroll>
  </div>
</template>

<style scoped lang="scss">
.title,
.subtitle {
  font-size: 0.8em;
}
.title {
  font-weight: bold;
}
.subtitle {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: var(--v-medium-emphasis-opacity);
}
.toggle-spacer {
  display: inline-block;
  width: 28px;
}
</style>
